/**
 * De beurt van begin tot eind.
 *
 * Volgorde (handboek p. 12-16, kennisbank 02 §2):
 *   gooien → gebeurtenis → opbrengst → hoofdfase → hand aanvullen → volgende speler
 *
 * Drie gebeurtenissen vragen een keuze van een speler. De kern zet die keuze klaar in
 * `state.pending` en wacht; zolang daar iets openstaat kan de beurt niet verder. Zo
 * blijft de kern puur: hij beslist niet voor de speler.
 */
import { BASIC_CARDS, LANDSCAPE_RESOURCE, LIMITS, type Seat } from '@catan/contracts';
import { destructibleBuildings } from './actioncards.js';
import { legLandschappen } from './building.js';
import {
  addResource,
  applyBrigands,
  leaderBy,
  naarEigenLandschap,
  removeResource,
} from './events.js';
import {
  applyGoodYear,
  applyNewYear,
  applyPlague,
  applyProgress,
  eventCardName,
  isProtectedByChurch,
  knightsAndFleets,
  takeBackCard,
  tuckUnderStack,
} from './eventcards.js';
import { heeftPlaatsbareBuit } from './playcard.js';
import { shuffle } from './rng.js';
import type { GameState, Pending, PlayerState, Side } from './state.js';
import { opponentOf } from './state.js';

/**
 * Welke gebouwde kaarten deze vraag mag aanwijzen.
 *
 * De Burgeroorlog gaat over ridders en vloten; een mislukte Vuurduivel over gebouwen — en
 * "vloten en ridders zijn geen gebouwen" (handboek p. 11 §2). Eén plek die dat beslist,
 * zodat de controle en de uitvoering het niet verschillend kunnen zien.
 */
export function terugnameKandidaten(
  // Alleen het vorstendom is nodig — zo kan ook het scherm hem aanroepen, dat geen
  // PlayerState heeft maar wél beide vorstendommen uit de GameView.
  speler: Pick<PlayerState, 'principality'>,
  soort: 'ridder-of-vloot' | 'gebouw' | undefined,
): { column: number; side: Side; cardId: number }[] {
  return soort === 'gebouw' ? destructibleBuildings(speler) : knightsAndFleets(speler);
}

/**
 * De naam van een kaart, voor het verslag.
 *
 * Alleen gebruiken waar de naam ook echt openbaar mág zijn — `geheimen.test.ts` speelt hele
 * potjes en bijt zodra een verslagregel een kaart noemt die nog verborgen in een hand ligt.
 */
const kaartNaamById = new Map(BASIC_CARDS.map((c) => [c.id, c.name]));
const naamVanKaart = (id: number): string => kaartNaamById.get(id) ?? 'kaart';

/** Wat een speler kan bijdragen aan de macht-tellingen. */
export interface CardStats {
  readonly commercePoints: (cardId: number) => number;
  readonly tournamentPoints: (cardId: number) => number;
  readonly strength: (cardId: number) => number;
  readonly isWarehouse: (cardId: number) => boolean;
}

function sumOverExpansions(player: PlayerState, value: (cardId: number) => number): number {
  return player.principality.buildings
    .flatMap((b) => [...b.expansions.boven, ...b.expansions.onder])
    .reduce((sum, cardId) => sum + value(cardId), 0);
}

export const commerceOf = (player: PlayerState, stats: CardStats): number =>
  sumOverExpansions(player, stats.commercePoints);

export const tournamentOf = (player: PlayerState, stats: CardStats): number =>
  sumOverExpansions(player, stats.tournamentPoints);

export const strengthOf = (player: PlayerState, stats: CardStats): number =>
  sumOverExpansions(player, stats.strength);

export interface EventOutcome {
  readonly state: GameState;
  /** Beschrijving van wat er gebeurde, voor de gebeurtenislog. */
  readonly log: readonly string[];
}

/**
 * Handelt de gegooide gebeurtenis af.
 *
 * Levert een stand op die óf klaar is voor de opbrengst, óf wacht op een keuze
 * (`state.pending`).
 */
export function resolveEvent(state: GameState, stats: CardStats): EventOutcome {
  const dice = state.dice;
  if (dice === undefined) throw new Error('er is nog niet gegooid');
  const log: string[] = [];

  switch (dice.event) {
    case 'roofoverval': {
      // Geldt niet in de eerste twee beurten (fysieke regels 1996).
      if (state.turnNumber < 2) {
        log.push('roofoverval — nog in de vrijstelling van de eerste twee beurten');
        return { state: { ...state, pending: undefined }, log };
      }
      const players = state.players.map((player) => {
        const result = applyBrigands(player, stats.isWarehouse);
        if (result.lost > 0) {
          log.push(`${player.name} verliest ${result.lost} grondstoffen aan de rovers`);
        }
        return result.player;
      }) as [PlayerState, PlayerState];
      if (log.length === 0) log.push('roofoverval — niemand had meer dan zeven onbeschermde grondstoffen');
      return { state: { ...state, players, pending: undefined }, log };
    }

    case 'goed-jaar': {
      // Beide spelers kiezen een grondstof; de speler aan zet begint.
      log.push('goed jaar — beide spelers kiezen een grondstof');
      return {
        state: { ...state, pending: { kind: 'kies-opbrengst', seat: state.turn, reason: 'goed-jaar' } },
        log,
      };
    }

    case 'riddertoernooi': {
      const winnaar = leaderBy(state, (p) => tournamentOf(p, stats));
      if (winnaar === undefined) {
        log.push('riddertoernooi — gelijkspel, niemand krijgt iets');
        return { state: { ...state, pending: undefined }, log };
      }
      log.push(`riddertoernooi — ${state.players[winnaar].name} heeft de meeste toernooipunten`);
      return {
        state: { ...state, pending: { kind: 'kies-opbrengst', seat: winnaar, reason: 'riddertoernooi' } },
        log,
      };
    }

    case 'handelsvoordeel': {
      // "Als de molen gedobbeld wordt, mag de speler mét de houten molen de ander een
      // grondstof afnemen. … Als niemand de molen bezit, vervalt deze gebeurtenis."
      // (p. 7b). Hier stond de kale handelspunten-leider — maar de molen zelf vereist
      // minstens één stad (p. 6), dus vóór de eerste stad kon je hier al stelen met
      // alleen een paar vloten. Het riddertoernooi is wél punten-gebaseerd (p. 7a);
      // die asymmetrie is van het boekje, niet van ons. Uit het zevende potje van Thijs.
      const winnaar = state.windmillToken;
      if (winnaar === undefined) {
        log.push('handelsvoordeel — niemand bezit de molen, de gebeurtenis vervalt');
        return { state: { ...state, pending: undefined }, log };
      }
      const beroofde = opponentOf(winnaar);
      // Niet alleen "ligt er iets" maar ook "kan de winnaar het kwijt" (p. 11 §5) —
      // anders opent hier een keuze zonder één geldig antwoord.
      if (!heeftPlaatsbareBuit(state.players[winnaar], state.players[beroofde])) {
        log.push(`handelsvoordeel — er ligt niets dat ${state.players[winnaar].name} kwijt kan`);
        return { state: { ...state, pending: undefined }, log };
      }
      log.push(`handelsvoordeel — ${state.players[winnaar].name} mag een grondstof afpakken`);
      return {
        state: {
          ...state,
          pending: { kind: 'kies-buit', seat: winnaar, from: beroofde, resterend: 1 },
        },
        log,
      };
    }

    case 'gebeurteniskaart': {
      const [getrokken, ...rest] = state.eventDeck;
      if (getrokken === undefined) {
        log.push('gebeurteniskaart — de stapel is leeg');
        return { state: { ...state, pending: undefined }, log };
      }
      // De kaart gaat onderop terug (fysieke regels: "returned to the bottom"). We
      // onthouden welke het was, want het scherm toont hem groot voordat hij werkt.
      const na: GameState = {
        ...state,
        eventDeck: [...rest, getrokken],
        pending: undefined,
        lastEventCard: getrokken,
      };
      return resolveEventCard(na, getrokken, log);
    }
  }
}

/**
 * Voert de werking van een getrokken gebeurteniskaart uit.
 *
 * Vier kaarten werken vanzelf; drie zetten een keuze klaar. Regelteksten staan bij de
 * implementaties in `eventcards.ts`.
 */
export function resolveEventCard(
  state: GameState,
  cardId: number,
  log: string[],
): EventOutcome {
  const naam = eventCardName(cardId);
  if (naam === undefined) {
    log.push(`onbekende gebeurteniskaart (${cardId})`);
    return { state, log };
  }
  log.push(`gebeurteniskaart: ${naam}`);
  const players = [...state.players] as [PlayerState, PlayerState];

  switch (naam) {
    case 'Pest': {
      for (const seat of [0, 1] as const) {
        const result = applyPlague(players[seat]);
        players[seat] = result.player;
        if (result.lost > 0) log.push(`${players[seat].name} verliest ${result.lost} grondstoffen aan de pest`);
      }
      return { state: { ...state, players }, log };
    }

    case 'Goed jaar': {
      for (const seat of [0, 1] as const) {
        const result = applyGoodYear(players[seat]);
        players[seat] = result.player;
        if (result.gained > 0) log.push(`${players[seat].name} krijgt ${result.gained} grondstoffen bij zijn pakhuizen`);
      }
      return { state: { ...state, players }, log };
    }

    case 'Vooruitgang': {
      let rng = state.rng;
      for (const seat of [0, 1] as const) {
        const result = applyProgress(players[seat], rng);
        players[seat] = result.player;
        rng = result.rng;
        if (result.gained > 0) log.push(`${players[seat].name} krijgt ${result.gained} grondstoffen door vooruitgang`);
      }
      return { state: { ...state, players, rng }, log };
    }

    case 'Jaarwisseling': {
      const geschud = applyNewYear(state.eventDeck, state.rng);
      log.push('de gebeurteniskaartenstapel is opnieuw geschud');
      return { state: { ...state, eventDeck: geschud.deck, rng: geschud.rng }, log };
    }

    case 'Burgeroorlog': {
      // "Iedere speler moet een ridderkaart of vloot terug in de hand nemen. De
      // tegenstander bepaalt welke kaart dat is. Bescherming: kerk."
      // De speler aan zet wijst als eerste aan bij zijn tegenstander.
      const doelwit = opponentOf(state.turn);
      if (isProtectedByChurch(players[doelwit])) {
        log.push(`${players[doelwit].name} is beschermd door zijn kerk`);
      } else if (knightsAndFleets(players[doelwit]).length === 0) {
        log.push(`${players[doelwit].name} heeft geen ridders of vloten staan`);
      } else {
        return {
          state: { ...state, pending: { kind: 'kies-terugname', seat: state.turn, from: doelwit } },
          log,
        };
      }
      // Niets te kiezen bij de een; dan meteen door naar de ander — en "de ander" is dan
      // de béúrtspeler als doelwit, met zijn tegenstander als kiezer. Hier stond
      // `opponentOf(state.turn)`: hetzelfde lege doelwit nog een keer, waardoor de hele
      // gebeurtenis verdampte zodra één kant niets had staan. Uit het vierde echte potje:
      // Chi had geen ridders of vloten, en Willem van Thijs bleef daardoor onterecht
      // gewoon staan — terwijl "iedere speler moet een kaart terugnemen" (p. 13 §13).
      return startCivilWarFor(state, state.turn, log);
    }

    case 'Conflict': {
      // "Wie de speelfiguur Ridder bezit, kiest uit de handkaarten van de tegenstander
      // 2 kaarten uit en schuift ze onder een willekeurige stapel."
      const houder = state.knightToken;
      if (houder === undefined) {
        log.push('niemand heeft de ridderfiguur — er gebeurt niets');
        return { state, log };
      }
      const doelwit = opponentOf(houder);
      if (players[doelwit].hand.length === 0) {
        log.push(`${players[doelwit].name} heeft geen handkaarten`);
        return { state, log };
      }
      const aantal = Math.min(2, players[doelwit].hand.length);
      return {
        state: { ...state, pending: { kind: 'kies-handkaarten', seat: houder, from: doelwit, aantal } },
        log,
      };
    }

    case 'Bouwmeester': {
      // "Iedere speler mag uit een uitbouwkaartenstapel een willekeurige handkaart
      // uitkiezen en geeft er één voor terug. De speler die aan de beurt is kiest eerst."
      log.push('bouwmeester — beide spelers ruilen een handkaart');
      const vraag = bouwmeesterVoor(state, state.turn, undefined, opponentOf(state.turn));
      if (vraag === undefined) {
        log.push('er is geen stapel om uit te kiezen');
        return { state, log };
      }
      return { state: { ...state, pending: vraag }, log };
    }
  }
}

/** Zoekt de volgende speler die bij Burgeroorlog iets moet inleveren. */
function startCivilWarFor(state: GameState, doelwit: Seat, log: string[]): EventOutcome {
  const kiezer = opponentOf(doelwit);
  if (isProtectedByChurch(state.players[doelwit])) {
    log.push(`${state.players[doelwit].name} is beschermd door zijn kerk`);
    return { state, log };
  }
  if (knightsAndFleets(state.players[doelwit]).length === 0) {
    log.push(`${state.players[doelwit].name} heeft geen ridders of vloten staan`);
    return { state, log };
  }
  return { state: { ...state, pending: { kind: 'kies-terugname', seat: kiezer, from: doelwit } }, log };
}

/**
 * Mag deze speler dit doelwit aanwijzen?
 *
 * Zonder deze toets keurt de kern elke `kies` goed. Dat lijkt onschuldig — `resolveChoice`
 * doet er dan toch niets mee — maar het levert een spel op dat vastloopt: wie een leeg
 * landschap aanwijst krijgt dezelfde vraag terug en kan die eindeloos herhalen. Een keuze
 * die niets uithaalt is geen zet.
 */
export function isLegalChoice(
  state: GameState,
  seat: Seat,
  target: { column: number; side: Side },
): { ok: boolean; reden?: string } {
  const pending = state.pending;
  if (pending === undefined) return { ok: false, reden: 'er staat geen keuze open' };
  if (pending.seat !== seat) return { ok: false, reden: 'deze keuze is niet aan jou' };

  const landschap = (p: PlayerState): { resources: number; landscape: string } | undefined =>
    p.principality.landscapes.find((t) => t.column === target.column && t.side === target.side);

  switch (pending.kind) {
    case 'kies-opbrengst': {
      // Een vol landschap mag je aanwijzen — dan gaat de opbrengst verloren, precies zoals
      // aan tafel. Een landschap dat niet van jou is, mag niet.
      return landschap(state.players[seat]) === undefined
        ? { ok: false, reden: 'dat is jouw landschap niet' }
        : { ok: true };
    }
    case 'kies-buit': {
      const tegel = landschap(state.players[pending.from]);
      if (tegel === undefined) return { ok: false, reden: 'dat landschap ligt daar niet' };
      if (tegel.resources === 0) return { ok: false, reden: 'daar ligt niets' };
      // "Grondstoffen kunnen alleen geroofd worden, als je ze op je landen van dat type
      // kunt plaatsen" (p. 11 §5). Zonder deze controle opende de `plaats-buit` hierna
      // met nul geldige antwoorden — een vraag waar niemand meer uit kon.
      const soort = LANDSCAPE_RESOURCE[tegel.landscape as keyof typeof LANDSCAPE_RESOURCE];
      return naarEigenLandschap(state.players[seat], soort) !== undefined
        ? { ok: true }
        : { ok: false, reden: `voor ${soort} heb je zelf geen plaats meer` };
    }
    case 'plaats-buit': {
      const tegel = landschap(state.players[seat]);
      if (tegel === undefined) return { ok: false, reden: 'dat is jouw landschap niet' };
      if (
        LANDSCAPE_RESOURCE[tegel.landscape as keyof typeof LANDSCAPE_RESOURCE] !==
        pending.resource
      ) {
        return { ok: false, reden: `${pending.resource} hoort op een ander landschap` };
      }
      // Een volle tegel kan er niets meer bij; zonder deze controle bleef de vraag
      // openstaan tot je toevallig een tegel met ruimte aanklikte — of voorgoed.
      return tegel.resources < LIMITS.resourcesPerLandscape
        ? { ok: true }
        : { ok: false, reden: 'dat landschap is vol' };
    }
    case 'handelaar-terug': {
      // Jouw gevulde tegel, en de ander moet de grondstof kunnen hebben (p. 12 §9).
      const tegel = landschap(state.players[seat]);
      if (tegel === undefined) return { ok: false, reden: 'dat is jouw landschap niet' };
      if (tegel.resources === 0) return { ok: false, reden: 'daar ligt niets om te geven' };
      const soort = LANDSCAPE_RESOURCE[tegel.landscape as keyof typeof LANDSCAPE_RESOURCE];
      return naarEigenLandschap(state.players[pending.from], soort) !== undefined
        ? { ok: true }
        : {
            ok: false,
            reden: `${state.players[pending.from].name} heeft geen plaats voor ${soort}`,
          };
    }
    case 'karavaan': {
      const tegel = landschap(state.players[seat]);
      if (tegel === undefined) return { ok: false, reden: 'dat is jouw landschap niet' };
      if (pending.stap === 'geef') {
        return tegel.resources > 0
          ? { ok: true }
          : { ok: false, reden: 'daar ligt niets om af te geven' };
      }
      return tegel.resources < LIMITS.resourcesPerLandscape
        ? { ok: true }
        : { ok: false, reden: 'dat landschap is vol' };
    }
    case 'leg-landschap': {
      // Je wijst de plek voor de eerste getrokken kaart aan: de buitenkolom naast het
      // nieuwe dorp, aan een kant die nog vrij is.
      if (target.column !== pending.column) {
        return { ok: false, reden: 'de landen komen naast het nieuwe dorp' };
      }
      const bezet = state.players[seat].principality.landscapes.some(
        (t) => t.column === target.column && t.side === target.side,
      );
      return bezet ? { ok: false, reden: 'daar ligt al een land' } : { ok: true };
    }
    case 'kies-terugname': {
      const staat = terugnameKandidaten(state.players[pending.from], pending.soort).some(
        (k) => k.column === target.column && k.side === target.side,
      );
      return staat
        ? { ok: true }
        : {
            ok: false,
            reden:
              pending.soort === 'gebouw'
                ? 'daar staat geen gebouw'
                : 'daar staat geen ridder of vloot',
          };
    }
    case 'kies-handkaarten': {
      // Hier is `column` de plaats in de hand van het doelwit.
      const hand = state.players[pending.from].hand;
      return target.column >= 0 && target.column < hand.length
        ? { ok: true }
        : { ok: false, reden: 'die handkaart bestaat niet' };
    }
    case 'kies-afleggen': {
      const hand = state.players[seat].hand;
      return target.column >= 0 && target.column < hand.length
        ? { ok: true }
        : { ok: false, reden: 'die handkaart bestaat niet' };
    }
    case 'kies-afleg-stapel':
      // Hier draagt `column` het nummer van de stapel.
      return target.column >= 0 && target.column < state.stacks.length
        ? { ok: true }
        : { ok: false, reden: 'die stapel bestaat niet' };
    case 'bouwmeester': {
      if (pending.stap === 'stapel') {
        const bestaat = target.column >= 0 && target.column < state.stacks.length;
        if (!bestaat) return { ok: false, reden: 'die stapel bestaat niet' };
        if (target.column === pending.verboden) {
          return { ok: false, reden: 'je tegenstander koos die stapel al' };
        }
        return (state.stacks[target.column]?.length ?? 0) > 0
          ? { ok: true }
          : { ok: false, reden: 'die stapel is leeg' };
      }
      if (pending.stap === 'kaart') {
        const stapel = state.stacks[pending.stack ?? -1] ?? [];
        return target.column >= 0 && target.column < stapel.length
          ? { ok: true }
          : { ok: false, reden: 'die kaart ligt niet in deze stapel' };
      }
      const hand = state.players[seat].hand;
      return target.column >= 0 && target.column < hand.length
        ? { ok: true }
        : { ok: false, reden: 'die handkaart bestaat niet' };
    }

    case 'handelaar': {
      // Je wijst een landschap van je tegenstander aan waar iets op ligt.
      const tegel = state.players[pending.from].principality.landscapes.find(
        (t) => t.column === target.column && t.side === target.side,
      );
      if (tegel === undefined) return { ok: false, reden: 'dat landschap ligt daar niet' };
      if (tegel.resources === 0) return { ok: false, reden: 'daar ligt niets' };
      // En je moet het ook kwíjt kunnen: "Beide spelers moeten voor de door de ruil te
      // verkrijgen grondstoffen plaats hebben op het type land waar ze grondstof van
      // krijgen. Als je bijvoorbeeld op je boskaart al 3 hout in voorraad hebt, kun je geen
      // hout meer krijgen" (handboek p. 12 §9). Anders dan bij `kies-buit` plaatst déze zet
      // zelf, dus de controle hoort hier: zonder haar bood het spel je een tegel aan die je
      // niet kunt verwerken, en verdween de buit onderweg.
      const soort = LANDSCAPE_RESOURCE[tegel.landscape as keyof typeof LANDSCAPE_RESOURCE];
      return naarEigenLandschap(state.players[seat], soort) !== undefined
        ? { ok: true }
        : { ok: false, reden: 'daar heb je geen plaats meer voor' };
    }

    case 'spion': {
      // Hier draagt `column` de plaats in de hand van je tegenstander.
      const hand = state.players[pending.from].hand;
      return target.column >= 0 && target.column < hand.length
        ? { ok: true }
        : { ok: false, reden: 'die handkaart bestaat niet' };
    }

    case 'kies-uit-stapel':
    case 'kies-startkaarten': {
      // Hier draagt `column` de plaats in de stapel die je doorkijkt.
      const stapel = state.stacks[pending.stack] ?? [];
      return target.column >= 0 && target.column < stapel.length
        ? { ok: true }
        : { ok: false, reden: 'die kaart ligt niet in deze stapel' };
    }
    case 'verkenner-kies': {
      // `column` is hier de plaats in de landschapstapel.
      return target.column >= 0 && target.column < state.landscapeDeck.length
        ? { ok: true }
        : { ok: false, reden: 'dat landschap ligt niet op de stapel' };
    }
    case 'kies-tegenkaart':
      // Die vraag beantwoord je met ja of nee, niet door iets aan te wijzen.
      return { ok: false, reden: 'antwoord eerst of je je tegenkaart inzet' };
    case 'verkenner-vraag':
      return { ok: false, reden: 'antwoord eerst of je de Verkenner inzet' };
  }
}

/**
 * De volgende stap van Bouwmeester, of niets als er niemand meer kan.
 *
 * Een speler kan alleen meedoen als er een stapel is die hij mag nemen — de stapel die
 * zijn tegenstander koos is voor hem geblokkeerd. Zonder deze controle blijft de vraag
 * openstaan terwijl er geen antwoord bestaat, en loopt het potje vast.
 */
function bouwmeesterVoor(
  state: GameState,
  seat: Seat,
  verboden: number | undefined,
  hierna: Seat | undefined,
): Pending | undefined {
  const kan = state.stacks.some((stapel, i) => stapel.length > 0 && i !== verboden);
  if (kan) {
    return { kind: 'bouwmeester', seat, stap: 'stapel', stack: undefined, verboden, hierna };
  }
  if (hierna === undefined) return undefined;
  return bouwmeesterVoor(state, hierna, verboden, undefined);
}

/**
 * De Handelaar rondt af: door naar de teruggave, of meteen klaar.
 *
 * "…en geef hem daar 1 grondstof van jouw keuze voor terug" (p. 12 §9). Die keuze is een
 * eigen vraag (`handelaar-terug`) — tenzij er niets te geven valt dat de ander ook kwijt
 * kan; dan vervalt de teruggave, net zoals het afpakken stopt zodra er niets haalbaars
 * meer ligt.
 */
function naarTeruggave(
  state: GameState,
  seat: Seat,
  from: Seat,
  log: string[],
): EventOutcome {
  if (heeftPlaatsbareBuit(state.players[from], state.players[seat])) {
    return {
      state: { ...state, pending: { kind: 'handelaar-terug', seat, from } },
      log,
    };
  }
  log.push(`er is niets terug te geven dat ${state.players[from].name} kan hebben`);
  return { state: { ...state, pending: undefined }, log };
}

/**
 * "Neem 1 **of 2** grondstoffen naar keuze af" — na de eerste mag je stoppen.
 *
 * Dit is het antwoord op de zet `kies-klaar`: de Handelaar-speler heeft er één gepakt en
 * ziet af van de tweede. De teruggave volgt gewoon.
 */
export function handelaarKlaar(state: GameState, seat: Seat): EventOutcome {
  const pending = state.pending;
  if (pending?.kind !== 'handelaar') throw new Error('er is nu geen handelaar bezig');
  if (pending.seat !== seat) throw new Error('deze keuze is niet aan jou');
  const log = [`${state.players[seat].name} laat het bij één grondstof`];
  return naarTeruggave(state, seat, pending.from, log);
}

/** Voert een keuze uit en bepaalt of er nog iets openstaat. */
export function resolveChoice(
  state: GameState,
  seat: Seat,
  target: { column: number; side: Side },
): EventOutcome {
  const pending = state.pending;
  if (pending === undefined) throw new Error('er staat geen keuze open');
  if (pending.seat !== seat) throw new Error('deze keuze is niet aan jou');

  const log: string[] = [];
  const players = [...state.players] as [PlayerState, PlayerState];

  switch (pending.kind) {
    case 'kies-opbrengst': {
      const result = addResource(players[seat], target.column, target.side);
      players[seat] = result.player;
      log.push(
        result.gained > 0
          ? `${players[seat].name} krijgt een grondstof (${pending.reason})`
          : `${players[seat].name} koos een vol landschap — geen grondstof`,
      );
      // Bij "goed jaar" is de tegenstander daarna aan de beurt met kiezen.
      const next: Pending | undefined =
        pending.reason === 'goed-jaar' && seat === state.turn
          ? { kind: 'kies-opbrengst', seat: opponentOf(seat), reason: 'goed-jaar' }
          : undefined;
      return { state: { ...state, players, pending: next }, log };
    }

    case 'kies-buit': {
      const result = removeResource(players[pending.from], target.column, target.side);
      if (result.taken === undefined) throw new Error('daar ligt niets om te pakken');
      players[pending.from] = result.player;
      log.push(`${players[seat].name} pakt ${result.taken} van ${players[pending.from].name}`);
      // Steelt deze kaart er meer dan één (de Rooftocht), dan draagt de `plaats-buit`
      // de teller verder — pas ná het plaatsen komt de volgende diefstal.
      const vervolg =
        pending.resterend > 1
          ? { hierna: { from: pending.from, resterend: pending.resterend - 1 } }
          : {};
      return {
        state: {
          ...state,
          players,
          pending: { kind: 'plaats-buit', seat, resource: result.taken, ...vervolg },
        },
        log,
      };
    }

    case 'kies-terugname': {
      const kandidaten = terugnameKandidaten(players[pending.from], pending.soort);
      const gekozen = kandidaten.find((k) => k.column === target.column && k.side === target.side);
      if (gekozen === undefined) throw new Error('daar staat geen kaart om terug te nemen');
      players[pending.from] = takeBackCard(
        players[pending.from],
        gekozen.column,
        gekozen.side,
        gekozen.cardId,
      );
      log.push(`${players[pending.from].name} neemt een kaart terug in de hand`);
      // Nu de andere speler, als die nog niet aan de beurt was.
      const tussen: GameState = { ...state, players, pending: undefined };
      if (pending.from === opponentOf(state.turn)) {
        return startCivilWarFor(tussen, state.turn, [...log]);
      }
      return { state: tussen, log };
    }

    case 'kies-handkaarten': {
      // Bij Conflict wijs je handkaarten aan; die reizen niet over een landschap, dus
      // de kolom draagt hier de index in de hand.
      const hand = players[pending.from].hand;
      const kaart = hand[target.column];
      if (kaart === undefined) throw new Error('die handkaart bestaat niet');
      const na = tuckUnderStack({ ...state, players }, pending.from, [kaart], 0);
      const restant = pending.aantal - 1;
      log.push(`${players[pending.seat].name} schuift een kaart van ${players[pending.from].name} onder een stapel`);
      return {
        state: {
          ...na,
          pending:
            restant > 0 && na.players[pending.from].hand.length > 0
              ? { kind: 'kies-handkaarten', seat: pending.seat, from: pending.from, aantal: restant }
              : undefined,
        },
        log,
      };
    }

    case 'kies-tegenkaart':
      throw new Error('de tegenkaartvraag beantwoord je met de zet "tegenkaart"');

    case 'verkenner-vraag':
      throw new Error('de verkennersvraag beantwoord je met de zet "verkenner"');

    case 'kies-afleggen': {
      const kaart = players[seat].hand[target.column];
      if (kaart === undefined) throw new Error('die handkaart bestaat niet');
      // Eerst de kaart, dan de stapel — twee klikken, net als in het origineel.
      log.push(`${players[seat].name} kiest een kaart om af te leggen`);
      return {
        state: { ...state, pending: { kind: 'kies-afleg-stapel', seat, cardId: kaart } },
        log,
      };
    }

    case 'bouwmeester': {
      if (pending.stap === 'stapel') {
        log.push(`${players[seat].name} kiest stapel ${target.column + 1}`);
        return {
          state: { ...state, pending: { ...pending, stap: 'kaart', stack: target.column } },
          log,
        };
      }
      if (pending.stap === 'kaart') {
        const stapel = state.stacks[pending.stack ?? -1] ?? [];
        const kaart = stapel[target.column];
        if (kaart === undefined) throw new Error('die kaart ligt niet in deze stapel');
        // De volgorde van de stapel blijft; alleen de gekozen kaart gaat eruit.
        const stapels = state.stacks.map((s2, i) =>
          i === pending.stack ? s2.filter((_, n) => n !== target.column) : s2,
        );
        players[seat] = { ...players[seat], hand: [...players[seat].hand, kaart] };
        log.push(`${players[seat].name} pakt een kaart`);
        return {
          state: { ...state, players, stacks: stapels, pending: { ...pending, stap: 'afgeven' } },
          log,
        };
      }
      // Afgeven: één handkaart gaat onder de gekozen stapel terug. Dat mag ook de kaart
      // zijn die je zojuist pakte — het handboek staat dat uitdrukkelijk toe.
      const kaart = players[seat].hand[target.column];
      if (kaart === undefined) throw new Error('die handkaart bestaat niet');
      const na = tuckUnderStack({ ...state, players }, seat, [kaart], pending.stack ?? 0);
      log.push(`${players[seat].name} geeft een kaart terug`);
      const volgende = pending.hierna;
      return {
        state: {
          ...na,
          pending:
            volgende === undefined
              ? undefined
              : bouwmeesterVoor(na, volgende, pending.stack, undefined),
        },
        log,
      };
    }

    case 'handelaar': {
      const weg = removeResource(players[pending.from], target.column, target.side);
      if (weg.taken === undefined) throw new Error('daar ligt niets');
      players[pending.from] = weg.player;
      // "Beide spelers moeten voor de door de ruil te verkrijgen grondstoffen plaats hebben
      // op het type land waar ze grondstof van krijgen. Als je bijvoorbeeld op je boskaart
      // al 3 hout in voorraad hebt, kun je geen hout meer krijgen" (handboek p. 12 §9).
      // Hier viel dat vroeger terug op `landscapes[0]`: had die geen ruimte, dan verdampte
      // de buit, en was hij van een ander type dan telde de grondstof daarna als dát type.
      const plek = naarEigenLandschap(players[seat], weg.taken);
      if (plek === undefined) throw new Error('daar heb je geen plaats voor');
      const bij = addResource(players[seat], ...plek);
      players[seat] = bij.player;
      log.push(`${players[seat].name} pakt ${weg.taken}`);

      const over = pending.resterend - 1;
      if (over > 0) {
        // Zelfde voorwaarde als bij het openen: is er niets meer te halen dat je kwijt kunt,
        // dan door naar de teruggave in plaats van een vraag met nul antwoorden.
        const nogIets = heeftPlaatsbareBuit(players[seat], players[pending.from]);
        if (nogIets) {
          return {
            state: { ...state, players, pending: { ...pending, resterend: over } },
            log,
          };
        }
      }
      return naarTeruggave({ ...state, players }, seat, pending.from, log);
    }

    case 'spion': {
      const hand = [...players[pending.from].hand];
      const kaart = hand[target.column];
      if (kaart === undefined) throw new Error('die handkaart bestaat niet');
      hand.splice(target.column, 1);
      players[pending.from] = { ...players[pending.from], hand };
      players[seat] = { ...players[seat], hand: [...players[seat].hand, kaart] };
      // De kaart bij naam, en dat is géén lek: de dief zág de hand (`magHandInzien` staat
      // dat toe zolang de vraag openstaat) en het slachtoffer kent zijn eigen kaarten. Er
      // is niemand aan tafel voor wie dit nieuw is.
      //
      // Zonder de naam stond er "steelt een handkaart" en zag de bestolene alleen zijn hand
      // krimpen — dat was een klacht uit de eerste live test. Het scherm haalt de naam hier
      // vandaan om de kaart groot in beeld te brengen bij het slachtoffer.
      log.push(`${players[seat].name} steelt de ${naamVanKaart(kaart)} uit de hand van ${players[pending.from].name}`);
      return { state: { ...state, players, pending: undefined }, log };
    }

    case 'kies-startkaarten': {
      // Je drie startkaarten, een voor een uit de gekozen stapel.
      const stapel = state.stacks[pending.stack] ?? [];
      const kaart = stapel[target.column];
      if (kaart === undefined) throw new Error('die kaart ligt niet in deze stapel');
      const stapels = state.stacks.map((s2, i) =>
        i === pending.stack ? s2.filter((_, n) => n !== target.column) : s2,
      );
      players[seat] = { ...players[seat], hand: [...players[seat].hand, kaart] };
      const over = pending.resterend - 1;
      log.push(
        over > 0
          ? `${players[seat].name} kiest een startkaart (nog ${over})`
          : `${players[seat].name} heeft zijn startkaarten`,
      );
      return {
        state: {
          ...state,
          players,
          stacks: stapels,
          pending:
            over > 0
              ? { kind: 'kies-startkaarten', seat, stack: pending.stack, resterend: over }
              : undefined,
        },
        log,
      };
    }

    case 'kies-uit-stapel': {
      // De stapel houdt zijn volgorde; alleen de gekozen kaart gaat eruit.
      const stapel = state.stacks[pending.stack] ?? [];
      const kaart = stapel[target.column];
      if (kaart === undefined) throw new Error('die kaart ligt niet in deze stapel');
      const stapels = state.stacks.map((s2, i) =>
        i === pending.stack ? s2.filter((_, n) => n !== target.column) : s2,
      );
      players[seat] = { ...players[seat], hand: [...players[seat].hand, kaart] };
      log.push(`${players[seat].name} kiest een kaart uit stapel ${pending.stack + 1}`);
      return { state: { ...state, players, stacks: stapels, pending: undefined }, log };
    }

    case 'verkenner-kies': {
      // Twee landschappen uit de stapel. Wélke, dat kiest de speler hier; wáár ze komen
      // (boven of onder) kiest hij daarna via dezelfde `leg-landschap`-vraag als bij een
      // gewoon dorp. Eerst legde de tweede stap ze meteen neer — de kiesvolgorde
      // bepaalde stilzwijgend de kanten, en dat voelde als loten (tweede livetest).
      const kaart = state.landscapeDeck[target.column];
      if (kaart === undefined) throw new Error('dat landschap ligt niet op de stapel');
      const stapel = state.landscapeDeck.filter((_, i) => i !== target.column);
      const gekozen = [...pending.gekozen, kaart];
      const ruimte = Math.min(2, stapel.length + gekozen.length);

      if (gekozen.length < Math.min(2, ruimte)) {
        log.push(`${players[seat].name} kiest een ${kaart.landscape} als eerste land`);
        return {
          state: {
            ...state,
            landscapeDeck: stapel,
            pending: { ...pending, zijde: 'onder', gekozen },
          },
          log,
        };
      }

      log.push(`${players[seat].name} heeft zijn landen uitgezocht`);
      // "Daarna schud je de stapel en legt deze gedekt terug" (handboek p. 11 §6). Dat is de
      // prijs van het doorzoeken: je hebt gezien wat er lag, dus die kennis mag niet blijven
      // hangen. Nergens anders in het spel wordt een stapel geschud — overal elders staat
      // juist "de volgorde mag niet veranderd worden".
      const geschud = shuffle(state.rng, stapel);
      return {
        state: {
          ...state,
          landscapeDeck: geschud.value,
          rng: geschud.rng,
          pending: {
            kind: 'leg-landschap',
            seat,
            column: pending.column + pending.outward,
            kaarten: gekozen,
          },
        },
        log,
      };
    }

    case 'kies-afleg-stapel': {
      const na = tuckUnderStack({ ...state, players }, seat, [pending.cardId], target.column);
      log.push(`${players[seat].name} legt een kaart onder stapel ${target.column + 1}`);
      return { state: { ...na, pending: undefined }, log };
    }

    case 'plaats-buit': {
      const tile = players[seat].principality.landscapes.find(
        (t) => t.column === target.column && t.side === target.side,
      );
      if (tile === undefined || LANDSCAPE_RESOURCE[tile.landscape] !== pending.resource) {
        throw new Error(`${pending.resource} hoort op een ander landschap`);
      }
      const result = addResource(players[seat], target.column, target.side);
      players[seat] = result.player;
      log.push(`${players[seat].name} legt de buit op zijn ${tile.landscape}`);
      // De Rooftocht steelt er twee: na het plaatsen volgt de tweede keuze — tenzij er
      // niets meer te halen valt dat de dief ook kwijt kan.
      if (pending.hierna !== undefined) {
        if (heeftPlaatsbareBuit(players[seat], players[pending.hierna.from])) {
          return {
            state: {
              ...state,
              players,
              pending: {
                kind: 'kies-buit',
                seat,
                from: pending.hierna.from,
                resterend: pending.hierna.resterend,
              },
            },
            log,
          };
        }
        log.push('er valt niets meer te halen dat de dief kwijt kan');
      }
      return { state: { ...state, players, pending: undefined }, log };
    }

    case 'handelaar-terug': {
      const weg = removeResource(players[seat], target.column, target.side);
      if (weg.taken === undefined) throw new Error('daar ligt niets om terug te geven');
      players[seat] = weg.player;
      const plek = naarEigenLandschap(players[pending.from], weg.taken);
      if (plek === undefined) throw new Error('daar heeft je tegenstander geen plaats voor');
      const bij = addResource(players[pending.from], ...plek);
      players[pending.from] = bij.player;
      log.push(`${players[seat].name} geeft ${weg.taken} terug aan ${players[pending.from].name}`);
      return { state: { ...state, players, pending: undefined }, log };
    }

    case 'karavaan': {
      if (pending.stap === 'geef') {
        const weg = removeResource(players[seat], target.column, target.side);
        if (weg.taken === undefined) throw new Error('daar ligt niets om af te geven');
        players[seat] = weg.player;
        log.push(`${players[seat].name} geeft ${weg.taken} af`);
        const over = pending.resterend - 1;
        // Na het afgeven volgt het terugnemen — er is dan gegarandeerd plaats, want de
        // zojuist geleegde tegels hebben zelf weer ruimte.
        const volgende: Pending =
          over > 0
            ? { ...pending, resterend: over }
            : { kind: 'karavaan', seat, stap: 'neem', resterend: 2 };
        return { state: { ...state, players, pending: volgende }, log };
      }
      const bij = addResource(players[seat], target.column, target.side);
      if (bij.gained === 0) throw new Error('dat landschap is vol');
      players[seat] = bij.player;
      const tegel = players[seat].principality.landscapes.find(
        (t) => t.column === target.column && t.side === target.side,
      );
      log.push(`${players[seat].name} neemt ${tegel === undefined ? 'een grondstof' : LANDSCAPE_RESOURCE[tegel.landscape]}`);
      const over = pending.resterend - 1;
      return {
        state: {
          ...state,
          players,
          pending: over > 0 ? { ...pending, resterend: over } : undefined,
        },
        log,
      };
    }

    case 'leg-landschap': {
      const eerste = pending.kaarten[0];
      if (eerste === undefined) throw new Error('er ligt niets meer te leggen');
      players[seat] = legLandschappen(players[seat], pending.column, pending.kaarten, target.side);
      log.push(
        pending.kaarten.length > 1
          ? `${players[seat].name} legt ${eerste.landscape} ${target.side}, ${pending.kaarten[1]?.landscape ?? ''} ${target.side === 'boven' ? 'onder' : 'boven'}`
          : `${players[seat].name} legt ${eerste.landscape} ${target.side}`,
      );
      return { state: { ...state, players, pending: undefined }, log };
    }
  }
}
